import { validateTimeRange } from "./timeValidation";

export const WEEKDAY_KEYS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];

export const WEEKDAY_LABELS = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

/** Short labels for compact display (Mon–Wed). */
export const WEEKDAY_SHORT = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Strip TIME / API string to HH:MM for HTML time inputs. */
export function timeToInputValue(t) {
  if (t == null || t === "") return "11:00";
  const s = String(t);
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "11:00";
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return "11:00";
  return `${pad2(h)}:${pad2(min)}`;
}

/** Normalize HH:MM from input to HH:MM:SS for API. */
export function inputValueToApiTime(v) {
  if (!v || typeof v !== "string") return "11:00:00";
  const p = v.length >= 5 ? v.slice(0, 5) : v;
  const m = p.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return "11:00:00";
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return "11:00:00";
  return `${pad2(h)}:${pad2(min)}:00`;
}

/**
 * Default: all days open with same hours (HH:MM for inputs).
 * @param {string} openInput
 * @param {string} closeInput
 */
export function createDefaultWeeklyHours(openInput = "11:00", closeInput = "02:00") {
  const open = inputValueToApiTime(timeToInputValue(openInput));
  const close = inputValueToApiTime(timeToInputValue(closeInput));
  const day = { closed: false, open, close };
  return {
    mon: { ...day },
    tue: { ...day },
    wed: { ...day },
    thu: { ...day },
    fri: { ...day },
    sat: { ...day },
    sun: { ...day },
  };
}

function normalizeDayFromApi(d) {
  if (!d || typeof d !== "object") return { closed: true };
  if (d.closed === true) return { closed: true };
  return {
    closed: false,
    open: inputValueToApiTime(timeToInputValue(d.open)),
    close: inputValueToApiTime(timeToInputValue(d.close)),
  };
}

/**
 * @param {object|null|undefined} bar
 * @returns {Record<string, { closed: boolean, open?: string, close?: string }>}
 */
export function weeklyHoursFromBar(bar) {
  let wh = bar?.weekly_hours;
  if (wh == null) {
    return createDefaultWeeklyHours(
      timeToInputValue(bar?.open_time),
      timeToInputValue(bar?.close_time)
    );
  }
  if (typeof wh === "string") {
    try {
      wh = JSON.parse(wh);
    } catch {
      return createDefaultWeeklyHours(
        timeToInputValue(bar?.open_time),
        timeToInputValue(bar?.close_time)
      );
    }
  }
  if (typeof wh !== "object" || wh === null) {
    return createDefaultWeeklyHours(
      timeToInputValue(bar?.open_time),
      timeToInputValue(bar?.close_time)
    );
  }
  const out = {};
  for (const key of WEEKDAY_KEYS) {
    out[key] = normalizeDayFromApi(wh[key]);
  }
  return out;
}

/**
 * Client-side validation; empty string if OK.
 * @param {object} wh
 * @returns {string}
 */
export function validateWeeklyHoursObject(wh) {
  if (!wh || typeof wh !== "object") {
    return "Invalid opening hours.";
  }
  let anyOpen = false;
  for (const key of WEEKDAY_KEYS) {
    const d = wh[key];
    const label = WEEKDAY_LABELS[key];
    if (!d || typeof d !== "object") {
      return `${label}: invalid entry.`;
    }
    if (d.closed === true) continue;
    if (d.closed !== false) {
      return `${label}: invalid closed flag.`;
    }
    anyOpen = true;
    const o = timeToInputValue(d.open);
    const c = timeToInputValue(d.close);
    if (!o || !c) {
      return `${label}: open and close times are required when not closed.`;
    }
    const v = validateTimeRange(o, c, { allowSameTime: false });
    if (!v.isValid) {
      return `${label}: ${v.error}`;
    }
  }
  if (!anyOpen) {
    return "At least one day must be open.";
  }
  return "";
}

/**
 * Payload for POST/PATCH (matches server expected shape).
 * @param {object} wh
 */
export function weeklyHoursToApiPayload(wh) {
  const out = {};
  for (const key of WEEKDAY_KEYS) {
    const d = wh[key];
    if (!d || d.closed === true) {
      out[key] = { closed: true };
    } else {
      out[key] = {
        closed: false,
        open: inputValueToApiTime(timeToInputValue(d.open)),
        close: inputValueToApiTime(timeToInputValue(d.close)),
      };
    }
  }
  return out;
}

/**
 * Group consecutive days with the same open/close for display.
 * @param {object} bar — row from API (may include weekly_hours JSON)
 * @param {(t: string) => string} formatTime — e.g. "2:30 PM"
 * @returns {string[]} lines to render
 */
export function getWeeklyHoursDisplayLines(bar, formatTime) {
  if (!formatTime || typeof formatTime !== "function") {
    return [];
  }
  let wh = bar?.weekly_hours;
  if (wh == null) {
    if (bar?.open_time || bar?.close_time) {
      return [
        `${formatTime(bar.open_time)} – ${formatTime(bar.close_time)} (daily)`,
      ];
    }
    return [];
  }
  if (typeof wh === "string") {
    try {
      wh = JSON.parse(wh);
    } catch {
      if (bar?.open_time || bar?.close_time) {
        return [
          `${formatTime(bar.open_time)} – ${formatTime(bar.close_time)} (daily)`,
        ];
      }
      return [];
    }
  }
  if (typeof wh !== "object" || wh === null) {
    return [];
  }

  const lines = [];
  let i = 0;
  while (i < WEEKDAY_KEYS.length) {
    const key = WEEKDAY_KEYS[i];
    const d = wh[key];
    if (!d || d.closed === true) {
      lines.push(`${WEEKDAY_SHORT[key]}: Closed`);
      i += 1;
      continue;
    }
    const open = d.open;
    const close = d.close;
    const sig = `${open}|${close}`;
    let j = i + 1;
    while (j < WEEKDAY_KEYS.length) {
      const k2 = WEEKDAY_KEYS[j];
      const d2 = wh[k2];
      if (!d2 || d2.closed === true) break;
      if (`${d2.open}|${d2.close}` !== sig) break;
      j += 1;
    }
    const a = WEEKDAY_SHORT[WEEKDAY_KEYS[i]];
    const b = WEEKDAY_SHORT[WEEKDAY_KEYS[j - 1]];
    const range = i === j - 1 ? a : `${a}–${b}`;
    lines.push(
      `${range}: ${formatTime(open)} – ${formatTime(close)}`
    );
    i = j;
  }
  return lines;
}
